import Image from 'next/future/image';
import { Dispatch, SetStateAction, useState } from 'react';
import { RC_DURATION, TagHelpers } from '@lambda/reebelo';
import cn from 'classnames';
import { CartLine } from '@lambda/commons/apis/shopify/types/storefront';
import { priceFormater } from '@/settings/index';
import {
  AddToCartParams,
  encodeDecodeForAttr,
  generateAddCartDateTimeAttr,
  generateIDAttr,
} from '@/lib/use-cart';
import { ProductWarrantyT } from '@/lib/reebelocare/types';
import {
  trackReebeloCareDeselected,
  trackReebeloCareSelected,
} from '@/lib/analytics/trackProduct';
import { ReebeloCareTrackRequest } from '@/lib/analytics/types';

const LineItem = ({
  device,
  rcToBeAdded,
  setRcToBeAdded,
  isCheckingOut,
  productWarranty,
}: {
  device: CartLine;
  rcToBeAdded: (AddToCartParams & { lineItemId: string })[];
  setRcToBeAdded: Dispatch<
    SetStateAction<(AddToCartParams & { lineItemId: string })[]>
  >;
  isCheckingOut: boolean;
  productWarranty: ProductWarrantyT;
}) => {
  const [checked, setChecked] = useState(false);

  const {
    sku,
    image: { url },
    product: { vendor, title },
  } = device.merchandise;

  const { category, psku } = {
    ...TagHelpers.extractTags(device.merchandise.product.tags),
    ...device.merchandise,
  };

  const handleToggle = () => {
    const trackingProps: ReebeloCareTrackRequest = {
      entry_point: 'Cart Pop-up',
      reebelocare_id: productWarranty[RC_DURATION['24_MONTH']].shopifyVariantId,
      device_psku: psku ?? '',
      device_vendor: vendor,
      device_sku: sku,
      duration: RC_DURATION['24_MONTH'],
    };

    setChecked((prevState) => {
      if (!prevState) {
        const toBeAdded: AddToCartParams & { lineItemId: string } = {
          merchandiseId: `gid://shopify/ProductVariant/${
            productWarranty[RC_DURATION['24_MONTH']].shopifyVariantId
          }`,
          attributes: [
            ...encodeDecodeForAttr.encode({
              sku,
              title,
            }),
            ...generateIDAttr(vendor, sku),
            ...generateAddCartDateTimeAttr(),
            {
              key: '_offer_category',
              value: category ?? '',
            },
          ],
          quantity: device.quantity,
          lineItemId: device.id,
        };

        const updatedRcToBeAdded = [...rcToBeAdded, toBeAdded];

        trackReebeloCareSelected(trackingProps);
        setRcToBeAdded(updatedRcToBeAdded);
      } else {
        const toBeRemoved = rcToBeAdded.findIndex(
          (item) =>
            item.merchandiseId ===
            `gid://shopify/ProductVariant/${
              productWarranty[RC_DURATION['24_MONTH']].shopifyVariantId
            }`,
        );

        const newRcToBeAdded = [...rcToBeAdded];

        newRcToBeAdded.splice(toBeRemoved, 1);

        trackReebeloCareDeselected(trackingProps);
        setRcToBeAdded(newRcToBeAdded);
      }

      return !prevState;
    });
  };

  if (!productWarranty) return null;

  const itemPrice = productWarranty[RC_DURATION['24_MONTH']].price;
  const itemMonthlyPrice = priceFormater.long(
    itemPrice / Number(RC_DURATION['24_MONTH']),
  );

  return (
    <button
      className={
        'group flex w-full items-center justify-between py-3 outline-none first:pt-0 last:pb-0 disabled:opacity-30'
      }
      disabled={isCheckingOut}
      type="button"
      onClick={() => handleToggle()}
    >
      <div className="flex items-center gap-x-4">
        <div
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded border ',
            {
              'border-gray-700/30 group-hover:border-gray-700': !checked,
              'border-gray-700': checked,
            },
          )}
        >
          {checked && (
            <svg
              width="10"
              height="7"
              viewBox="0 0 10 7"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.79037 0.580566L2.95703 6.41973L1.20703 4.66973"
                stroke="#1F2323"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
        <div className="text-left leading-tight">
          <h4 className="text-sm font-bold">
            Protect my {title.split(' - ')[0]}
          </h4>
          <span className="text-sm">
            {priceFormater.long(productWarranty[RC_DURATION['24_MONTH']].price)}{' '}
            ({itemMonthlyPrice}/mo)
          </span>
        </div>
      </div>

      <div className="h-16 w-16 rounded-xl bg-[#F2F7F9] p-2">
        <div className="h-full w-full">
          <Image
            src={url}
            priority
            loading="eager"
            alt={title}
            height={300}
            width={300}
            className="h-full w-full object-contain"
          />
        </div>
      </div>
    </button>
  );
};

export default LineItem;
